import { useCallback } from 'react';

import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  FormControl,
  makeStyles,
  TextField,
} from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import { useFormik } from 'formik';
import { useSnackbar } from 'notistack';

import DialogForm from 'components/DialogForm';
import DialogHeader from 'components/DialogHeader';
import { useAppDispatch, useAppSelector } from 'store';
import {
  fetchFilaV2,
  setAtendimentoSelecionado,
  atendimentosSelector,
} from 'store/reducers/atendimentos';
import {
  ContatoInterface,
  contatosSelector,
  definirEquipeOuAtendentePadrao,
  fetchContatos,
  resetContatos,
} from 'store/reducers/contatos';
import {
  EquipeInterface,
  equipesSelector,
  fetchEquipes,
} from 'store/reducers/equipes';
import {
  fetchAllUsuarios,
  resetAllAtendentes,
  usuariosSelector,
} from 'store/reducers/usuarios';
import { getUrlParams } from 'utils/getUrlParams';
import * as validators from 'utils/validators';

const useStyles = makeStyles(theme => ({
  removerEquipePadraoButton: {
    color: theme.palette.error.main,

    '&:disabled': {
      color: theme.palette.action.disabled,
    },
  },
  dialogActions: { display: 'flex', justifyContent: 'space-between' },
}));

type ModalDefinirEquipePadraoProps = {
  open: boolean;
  onClose: () => void;
  onSuccess?: (novoContato: ContatoInterface) => void;
  onRemoverEquipePadrao?: () => void;
  contatoId: string | undefined;
};

function ModalDefinirEquipePadrao(props: ModalDefinirEquipePadraoProps) {
  const { open, onClose, onSuccess, onRemoverEquipePadrao, contatoId } = props;

  const { enqueueSnackbar } = useSnackbar();
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const { equipes, loadingEquipes } = useAppSelector(equipesSelector);
  const { allAtendentes, loadingAllAtendentes } =
    useAppSelector(usuariosSelector);

  const { atendimentoSelecionado } = useAppSelector(atendimentosSelector);
  const { contatoAtendimentoV2 } = useAppSelector(contatosSelector);
  const { orderBy } = getUrlParams();

  const formik = useFormik({
    initialValues: {
      equipeId: '',
      usuarioId: '',
    },
    validationSchema: validators.definirEquipePadraoSchema,
    onSubmit: async ({ equipeId, usuarioId }) => {
      if (contatoId) {
        await dispatch(
          definirEquipeOuAtendentePadrao({
            contatoId,
            equipeId,
            usuarioId: usuarioId || null,
          }),
        )
          .unwrap()
          .then(contatoAlterado => {
            if (!onSuccess) {
              dispatch(resetContatos());
              dispatch(fetchContatos());
            }

            dispatch(fetchFilaV2({ orderBy }));

            if (atendimentoSelecionado) {
              dispatch(
                setAtendimentoSelecionado({
                  ...atendimentoSelecionado,
                  contato: contatoAlterado,
                }),
              );
            }

            onSuccess?.(contatoAlterado);
            enqueueSnackbar('Operação realizada com sucesso.');
          })
          .catch(error => {
            if (error.response) enqueueSnackbar(error.response.data.message);
          })
          .finally(() => {
            onClose();
          });
      }
    },
  });

  const handleChangeEquipe = useCallback(
    (value: EquipeInterface | null) => {
      formik.setFieldValue('equipeId', value?.id || '');
      formik.setFieldValue('usuarioId', '');

      if (value) {
        dispatch(fetchAllUsuarios(value.id));
      } else {
        dispatch(resetAllAtendentes());
      }
    },
    [dispatch, formik],
  );

  const handleDialogEnter = useCallback(() => {
    dispatch(fetchEquipes())
      .unwrap()
      .then(equipes => {
        if (!contatoAtendimentoV2?.teamDefault) return;

        const equipe = equipes.find(
          e => e.id === contatoAtendimentoV2.teamDefault?.id,
        );

        if (!equipe) return;

        formik.setFieldValue('equipeId', equipe.id);

        dispatch(fetchAllUsuarios(equipe.id))
          .unwrap()
          .then(atendentes => {
            if (!contatoAtendimentoV2.atendenteDefault) return;

            const atendente = atendentes.find(
              a => a.id === contatoAtendimentoV2.atendenteDefault?.id,
            );

            if (atendente) {
              formik.setFieldValue('usuarioId', atendente.id);
            }
          });
      });
  }, [dispatch, formik, contatoAtendimentoV2]);

  const handleDialogExit = useCallback(() => {
    formik.resetForm();
    dispatch(resetAllAtendentes());
  }, [formik, dispatch]);

  const equipeIdError = formik.touched.equipeId && formik.errors.equipeId;
  const usuarioIdError = formik.touched.usuarioId && formik.errors.usuarioId;

  const hasEquipeId = !!formik.values.equipeId;
  const equipeValue =
    equipes?.find(e => e.id === formik.values.equipeId) || null;
  const usuarioValue =
    allAtendentes?.find(a => a.id === formik.values.usuarioId) || null;

  return (
    <Dialog
      fullWidth
      maxWidth="xs"
      open={open}
      onClose={onClose}
      TransitionProps={{
        onEnter: handleDialogEnter,
        onExited: handleDialogExit,
      }}
    >
      <DialogHeader
        title="Definir atendente padrão"
        description="Defina uma equipe ou atendente padrão para novos atendimentos. Se nenhum atendente for escolhido, o atendimento ficará disponível para todos da equipe."
        onClose={onClose}
      />

      <DialogForm onSubmit={formik.handleSubmit} loading={formik.isSubmitting}>
        <DialogContent>
          <FormControl
            fullWidth
            variant="outlined"
            margin="none"
            error={Boolean(equipeIdError)}
          >
            <Autocomplete
              id="auto-complete-equipe"
              options={equipes || []}
              value={equipeValue}
              getOptionLabel={option => option?.nome || ''}
              onChange={(_e, value) => handleChangeEquipe(value)}
              disabled={loadingEquipes}
              includeInputInList
              noOptionsText="Nenhuma equipe encontrada"
              renderInput={params => (
                <TextField
                  {...params}
                  fullWidth
                  name="equipeId"
                  label="Equipe"
                  variant="outlined"
                  error={Boolean(equipeIdError)}
                  helperText={equipeIdError}
                />
              )}
            />
          </FormControl>

          {hasEquipeId && (
            <FormControl
              fullWidth
              variant="outlined"
              margin="normal"
              error={Boolean(usuarioIdError)}
            >
              <Autocomplete
                id="auto-complete-usuario"
                options={allAtendentes || []}
                value={usuarioValue}
                getOptionLabel={option => option?.nome || ''}
                onChange={(_e, value) => {
                  formik.setFieldValue('usuarioId', value?.id || '');
                }}
                disabled={loadingAllAtendentes}
                includeInputInList
                noOptionsText="Nenhum atendente encontrado"
                renderInput={params => (
                  <TextField
                    {...params}
                    fullWidth
                    name="usuarioId"
                    label="Atendente (opcional)"
                    variant="outlined"
                    error={Boolean(usuarioIdError)}
                    helperText={usuarioIdError}
                  />
                )}
              />
            </FormControl>
          )}
        </DialogContent>

        <DialogActions className={classes.dialogActions}>
          <Button
            onClick={() => {
              onRemoverEquipePadrao?.();
              onClose();
            }}
            className={classes.removerEquipePadraoButton}
            variant="text"
            disabled={contatoAtendimentoV2?.teamDefault === null}
          >
            Remover
          </Button>
          <Box display="flex" gridGap={8}>
            <Button onClick={onClose}>Cancelar</Button>
            <Button color="primary" variant="contained" type="submit">
              Salvar
            </Button>
          </Box>
        </DialogActions>
      </DialogForm>
    </Dialog>
  );
}

export default ModalDefinirEquipePadrao;
